import React, {useState, useEffect, useMemo} from 'react';
import {FmlxTable} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import moment from 'moment';
import {Button} from 'react-bootstrap';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {DrawerButtonTitle} from '../Common';

import {HeaderLayout} from '../../components';
import FastModal from '../../components/FastModal';
import PageLayout from '../../components/Layout/PageLayout';

import {Feedback} from '../../constants/dataEnum';

import withIntl from '../../lib/withIntl';

import {hideLoading, setOverrideBeforeUnload, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {
  prepareDownloadLogByDate,
  prepareDownloadLogByIds,
  downloadLog,
  getLogList,
} from '../../stores/business/errorReportingBusiness';

import './LogList.scss';

const COLUMNS = [
  {Header: 'Log Name', accessor: 'logName'},
  {Header: 'Date', accessor: 'date'},
  {Header: 'Time', accessor: 'time'},
];

const mapDispatchToProps = (dispatch) => ({
  onHideLoading: bindActionCreators(hideLoading, dispatch),
  onShowLoading: bindActionCreators(showLoading, dispatch),
  onShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  onOverrideBeforeUnload: bindActionCreators(setOverrideBeforeUnload, dispatch),
  errorReportingBusinessPrepareDownloadLogByDate: bindActionCreators(prepareDownloadLogByDate, dispatch),
  errorReportingBusinessPrepareDownloadLogByIds: bindActionCreators(prepareDownloadLogByIds, dispatch),
  errorReportingBusinessDownloadLog: bindActionCreators(downloadLog, dispatch),
  errorReportingBusinessGetLogList: bindActionCreators(getLogList, dispatch),
});

function LogList({
  onHideLoading,
  onShowLoading,
  onShowSnackbar,
  onOverrideBeforeUnload,
  errorReportingBusinessPrepareDownloadLogByDate,
  errorReportingBusinessPrepareDownloadLogByIds,
  errorReportingBusinessDownloadLog,
  errorReportingBusinessGetLogList,
  intl,
}) {
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortValue, setSortValue] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [createPackageDialogOpen, setCreatePackageDialogOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [fromTime, setFromTime] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [disabledCreatePackageModalButton, setDisabledCreatePackageModalButton] = useState(true);

  useEffect(() => {
    setDisabledCreatePackageModalButton(!date || !fromTime || !toTime);
  }, [date, fromTime, toTime]);

  const fetchData = async () => {
    setIsLoading(true);

    const result = await errorReportingBusinessGetLogList();

    let data = [];

    if (result) {
      data = result.map((e) => {
        const {id, name, createdTime} = e;
        const dateTimeObj = moment(createdTime);
        const dateString = dateTimeObj.format('MM/DD/YYYY');
        const timeString = dateTimeObj.format('HH:mm');

        return {
          id,
          dateTimeObj,
          logName: name,
          date: dateString,
          time: timeString,
        };
      });
    }

    setDataSource(data);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let sortedDataSource = [...dataSource];

    sortedDataSource = sortedDataSource.sort((a, b) => {
      let objA = a[sortValue];
      let objB = b[sortValue];

      switch (sortValue) {
        case 'date':
          objA = a.dateTimeObj;
          objB = b.dateTimeObj;

          break;
        case 'time':
          objA = a.dateTimeObj.format('HH:mm');
          objB = b.dateTimeObj.format('HH:mm');

          break;
        default:
          break;
      }

      if (objA > objB) {
        return 1;
      }

      if (objB > objA) {
        return -1;
      }

      return 0;
    });

    if (sortType === FmlxTable.SortType.DESC) {
      sortedDataSource = sortedDataSource.reverse();
    }

    setDataSource(sortedDataSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortValue, sortType]);

  const doDownload = async (byIds, arg1, arg2) => {
    onShowLoading(intl.formatMessage({id: 'logList.download.preparing'}));

    const func = byIds ? errorReportingBusinessPrepareDownloadLogByIds : errorReportingBusinessPrepareDownloadLogByDate;

    const result = await func(arg1, arg2);

    onHideLoading();

    // errorCode === 0 => FastErrorCode.NoError
    if (!result || result.errorCode !== 0) {
      onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'logList.download.notification.error'}));

      return false;
    }

    onOverrideBeforeUnload(true);
    errorReportingBusinessDownloadLog(result.token);
    onOverrideBeforeUnload(false);

    onShowSnackbar(Feedback.INFO, intl.formatMessage({id: 'logList.download.notification.started'}));

    return true;
  };

  const downloadHandler = () => {
    const ids = selectedRows.map((e) => e.id);

    doDownload(true, ids);
  };

  const createLogPackageHandler = () => {
    setCreatePackageDialogOpen(true);
  };

  const onCreatePackageClickHandler = async () => {
    const dateObj = moment(date);
    const fromTimeObj = moment(fromTime);
    const toTimeObj = moment(toTime);

    const isUntilMidnightTomorrow = toTimeObj.format('HH:mm') === '23:59';

    const fromTimeString = fromTimeObj.format('HH:mm');
    const toTimeString = isUntilMidnightTomorrow ? '00:00' : toTimeObj.format('HH:mm');

    const dateStringFrom = dateObj.format('YYYY-MM-DD');
    const dateStringTo = isUntilMidnightTomorrow ? dateObj.add(1, 'd').format('YYYY-MM-DD') : dateStringFrom;

    const startDate = moment(`${dateStringFrom} ${fromTimeString}`).toISOString();
    const endDate = moment(`${dateStringTo} ${toTimeString}`).toISOString();

    // to inActive beforeUnload
    onOverrideBeforeUnload(true);
    // open file dialog and generate download file
    const result = await doDownload(false, startDate, endDate);
    // to reActivated beforeUnload
    onOverrideBeforeUnload(false);

    if (!result) {
      return;
    }

    setCreatePackageDialogOpen(false);
  };

  const memoizedDataSource = useMemo(() => dataSource, [dataSource]);
  const memoizedColumns = useMemo(() => COLUMNS, []);

  useEffect(() => {
    const onHandleModalEnter = (event) => {
      if (event.key === 'Enter') {
        if (createPackageDialogOpen && !disabledCreatePackageModalButton) {
          onCreatePackageClickHandler();
        }
      }
    };

    if (createPackageDialogOpen && !disabledCreatePackageModalButton) {
      document.addEventListener('keydown', onHandleModalEnter);
    }

    return () => {
      if (createPackageDialogOpen && !disabledCreatePackageModalButton) {
        document.removeEventListener('keydown', onHandleModalEnter);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledCreatePackageModalButton, createPackageDialogOpen]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <HeaderLayout>
          <HeaderLayout.Left>
            <DrawerButtonTitle title={intl.formatMessage({id: 'logList.title'})} />
          </HeaderLayout.Left>
          <HeaderLayout.Right>
            <Button
              className="log-list-button secondary-button"
              disabled={!selectedRows.length}
              onClick={downloadHandler}
            >
              {intl.formatMessage({id: 'logList.header.download'})}
            </Button>
            <Button className="log-list-button primary-button" onClick={createLogPackageHandler}>
              {intl.formatMessage({id: 'logList.header.createLogPackage'})}
            </Button>
          </HeaderLayout.Right>
        </HeaderLayout>
      </PageLayout.Header>
      <PageLayout.Content>
        <FmlxTable
          type={FmlxTable.Type.MULTISELECT}
          data={memoizedDataSource}
          columns={memoizedColumns}
          selectable
          multiSelect
          showCheckBoxSelection
          getSelectedRow={(selectedItems) => {
            setSelectedRows(selectedItems);
          }}
          getSortValue={(e) => {
            setSortValue(e.column);
            setSortType(e.type);
          }}
          sortType={sortType}
          enableDragSelection={false}
          showPagination={false}
          pageSizes={[10, 20, 30, 40, 50]}
          placeholder={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  color: '#707070',
                  marginTop: 40,
                }}
              >
                {intl.formatMessage({id: 'logList.logEmpty'})}
              </div>
            </div>
          }
          isLoading={isLoading}
          showToolbar={false}
        />
      </PageLayout.Content>
      <FastModal
        header={intl.formatMessage({id: 'logList.createLogPackageModal.title'})}
        content={
          <div className="log-list__modal__create-package">
            <div style={{display: 'flex', alignItems: 'center', margin: '20px 0px'}}>
              <div style={{width: 110}}>{intl.formatMessage({id: 'logList.createLogPackageModal.selectDate'})}</div>
              <div style={{width: '100%'}}>
                <DatePicker showPopperArrow={false} selected={date} onChange={setDate} />
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', margin: '20px 0px'}}>
              <div style={{width: 110}}>{intl.formatMessage({id: 'logList.createLogPackageModal.fromTime'})}</div>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <div style={{width: '30%', paddingRight: '20px'}}>
                  <DatePicker
                    showPopperArrow={false}
                    selected={fromTime}
                    onChange={(e) => {
                      setFromTime(e);

                      const fromTimeObj = moment(e);
                      const toTimeObj = moment(toTime);

                      const timeDiff = fromTimeObj.diff(toTimeObj, 'seconds');

                      if (timeDiff >= 0) {
                        setToTime(null);
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={60}
                    timeCaption="From Hour"
                    dateFormat="hh:mm aa"
                  />
                </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <div style={{width: 110}}>{intl.formatMessage({id: 'logList.createLogPackageModal.toTime'})}</div>
                  <div style={{width: '100%', marginRight: '12px'}}>
                    <DatePicker
                      showPopperArrow={false}
                      selected={toTime}
                      onChange={setToTime}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={60}
                      timeCaption="To Hour"
                      dateFormat="hh:mm aa"
                      timeClassName={(time) => time.getHours() === 0 && 'hidden'}
                      filterTime={(time) => {
                        if (!fromTime) {
                          return true;
                        }

                        const currentDate = moment(fromTime).toDate();
                        const selectedDate = moment(time).toDate();

                        return currentDate.getTime() < selectedDate.getTime();
                      }}
                      injectTimes={[setHours(setMinutes(new Date(), 59), 23)]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        btnAction={[
          {
            id: 'btn-create-package',
            text: intl.formatMessage({id: 'logList.createLogPackageModal.ok'}),
            style: 'primary',
            handlerClick: onCreatePackageClickHandler,
            disabled: disabledCreatePackageModalButton,
          },
        ]}
        show={createPackageDialogOpen}
        isValid
        onClose={() => {
          setCreatePackageDialogOpen(false);
        }}
        closeDisabled={false}
      />
    </PageLayout>
  );
}

LogList.propTypes = {
  onHideLoading: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  onOverrideBeforeUnload: PropTypes.func.isRequired,
  errorReportingBusinessPrepareDownloadLogByDate: PropTypes.func.isRequired,
  errorReportingBusinessPrepareDownloadLogByIds: PropTypes.func.isRequired,
  errorReportingBusinessDownloadLog: PropTypes.func.isRequired,
  errorReportingBusinessGetLogList: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
};

LogList.defaultProps = {};

export default connect(null, mapDispatchToProps)(withIntl(LogList));
